import { defineStore } from 'pinia';
import { ref } from 'vue';
import {
  SalesRepInterface,
  CreateSalesRepInterface,
  UpdateSalesRepInterface,
  PaginatedSalesRepParams,
} from '@/types/SalesRepInterface';
import SalesRepService from '@/services/SalesRepService';

export const useSalesRepStore = defineStore('SalesRepStore', () => {
  const salesReps = ref<SalesRepInterface[]>([]);
  const loading = ref(false);
  const error = ref<unknown | null>(null);
  const paginatedSalesReps = ref<SalesRepInterface[]>([]);
  const totalRecords = ref(0);

  async function fetchSalesReps() {
    loading.value = true;
    error.value = null;
    const res = await SalesRepService.getSalesReps().catch((err) => {
      error.value = err;
      loading.value = false;
      console.error('Failed to fetch sales reps:', err);
      throw err;
    });
    salesReps.value = res;
    loading.value = false;
  }

  async function createSalesRep(data: CreateSalesRepInterface) {
    loading.value = true;
    error.value = null;
    await SalesRepService.createSalesRep(data).catch((err) => {
      error.value = err;
      loading.value = false;
      console.error('Failed to create sales rep:', err);
      throw err;
    });
    await fetchSalesReps();
    loading.value = false;
  }

  async function updateSalesRep(id: number, data: UpdateSalesRepInterface) {
    loading.value = true;
    error.value = null;
    await SalesRepService.updateSalesRep(id, data).catch((err) => {
      error.value = err;
      loading.value = false;
      console.error('Failed to update sales rep:', err);
      throw err;
    });
    await fetchSalesReps();
    loading.value = false;
  }

  async function toggleSalesRepStatus(id: number, isActive: boolean) {
    loading.value = true;
    error.value = null;
    await SalesRepService.updateSalesRep(id, { active: isActive ? 1 : 0 }).catch((err) => {
      error.value = err;
      loading.value = false;
      console.error('Failed to toggle sales rep status:', err);
      throw err;
    });
    await fetchSalesReps();
    loading.value = false;
  }

  async function fetchPaginatedSalesReps(params: PaginatedSalesRepParams) {
    loading.value = true;
    error.value = null;
    const res = await SalesRepService.getPaginatedSalesReps({
      ...params,
      paginate: true,
    }).catch((err) => {
      error.value = err;
      loading.value = false;
      console.error('Failed to fetch paginated sales reps:', err);
      throw err;
    });
    paginatedSalesReps.value = res.data;
    totalRecords.value = res.meta.total;
    loading.value = false;
  }

  return {
    salesReps,
    loading,
    error,
    fetchSalesReps,
    createSalesRep,
    updateSalesRep,
    toggleSalesRepStatus,
    fetchPaginatedSalesReps,
    paginatedSalesReps,
    totalRecords,
  };
});

export default useSalesRepStore;
